import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable register-interest modal.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - type: "variety" | "workshop" | "tuber-release" | "mystery-box" | "named-varieties" | "general"
 *  - target: optional id (e.g. variety_id)
 *  - targetLabel: human-readable subject ("Café au Lait" or "Workshops")
 *  - title, subtitle: optional copy overrides
 */
export default function InterestModal({
  open,
  onClose,
  type = "general",
  target = "",
  targetLabel = "",
  title,
  subtitle,
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setEmail("");
      setNotes("");
      setBusy(false);
    }
  }, [open]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    try {
      setBusy(true);
      await axios.post(`${API}/interest`, {
        name: name.trim(),
        email: email.trim(),
        type,
        target,
        target_label: targetLabel,
        notes: notes.trim(),
      });
      toast.success("Thank you, we'll be in touch.");
      onClose?.();
    } catch (err) {
      toast.error("Couldn't register your interest just now.");
    } finally {
      setBusy(false);
    }
  };

  const heading =
    title ||
    (targetLabel ? `Register interest in ${targetLabel}` : "Register your interest");

  return (
    <div
      data-testid="interest-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-black/55 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)] wobble-a border-2 border-[var(--dahlias-sage)]/40 shadow-2xl p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="interest-modal-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 wobble-blob bg-[var(--dahlias-sage)]/10 hover:bg-[var(--dahlias-sage)]/25 flex items-center justify-center text-[var(--dahlias-forest)] transition"
        >
          <X className="w-5 h-5" />
        </button>

        <span className="font-label tracking-[0.4em] text-[10px] text-[var(--dahlias-sage-deep)]">
          ✦ REGISTER INTEREST ✦
        </span>
        <h3
          data-testid="interest-modal-heading"
          className="mt-2 font-display text-2xl md:text-3xl text-[var(--dahlias-forest)] leading-tight pr-8"
        >
          {heading}
        </h3>
        {subtitle && (
          <p className="mt-3 text-sm md:text-base text-[var(--dahlias-ink)]/70 leading-relaxed">
            {subtitle}
          </p>
        )}

        <form
          onSubmit={submit}
          data-testid="interest-form"
          className="mt-5 space-y-4"
        >
          <div>
            <Label
              htmlFor="i-name"
              className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]"
            >
              Your name
            </Label>
            <Input
              id="i-name"
              data-testid="interest-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="First & last"
              className="mt-2 h-12 text-base bg-[var(--dahlias-paper)] border-[var(--dahlias-sage)]/30 wobble-pill px-5"
            />
          </div>
          <div>
            <Label
              htmlFor="i-email"
              className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]"
            >
              Email
            </Label>
            <Input
              id="i-email"
              type="email"
              data-testid="interest-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@somewhere.nz"
              className="mt-2 h-12 text-base bg-[var(--dahlias-paper)] border-[var(--dahlias-sage)]/30 wobble-pill px-5"
            />
          </div>
          <div>
            <Label
              htmlFor="i-notes"
              className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]"
            >
              Anything to add <span className="opacity-50">(optional)</span>
            </Label>
            <Textarea
              id="i-notes"
              data-testid="interest-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Quantity, colour you're after, the garden it's going in…"
              className="mt-2 text-base bg-[var(--dahlias-paper)] border-[var(--dahlias-sage)]/30 wobble-a"
            />
          </div>
          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={busy}
              data-testid="interest-submit"
              className="h-12 px-7 text-base bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill font-medium tracking-wide shadow-md inline-flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {busy ? "Sending…" : "Register interest"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
